import React, { useState, useEffect } from "react";
import { Container, Card, Form, Button, Alert, InputGroup } from "react-bootstrap";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { useI18n } from "../i18n";
import { validatePassword } from "../utils/passwordValidation";

const PasswordResetConfirm: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const { t } = useI18n();
  const navigate = useNavigate();

  const token = searchParams.get("token");

  useEffect(() => {
    if (!token) {
      setError(t("passwordReset.invalidToken"));
    }
  }, [token, t]);

  const handlePasswordChange = (password: string) => {
    setNewPassword(password);
    const errors = validatePassword(password, t);
    setValidationErrors(errors);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    // Validate passwords match
    if (newPassword !== confirmPassword) {
      setError(t("passwordReset.passwordMismatch"));
      return;
    }

    // Validate password complexity
    if (validationErrors.length > 0) {
      setError(t("passwordReset.passwordRequirements"));
      return;
    }

    if (!token) {
      setError(t("passwordReset.invalidToken"));
      return;
    }

    setLoading(true);

    try {
      await axios.post("/api/auth/password-reset-confirm", {
        token,
        new_password: newPassword,
      });
      setSuccess(true);
      setTimeout(() => navigate("/login"), 3000);
    } catch (err: any) {
      setError(
        err.response?.data?.detail || t("passwordReset.resetError")
      );
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <Container className="mt-5">
        <Card style={{ maxWidth: "500px", margin: "0 auto" }}>
          <Card.Body className="text-center p-5">
            <div className="mb-4">
              <i className="bi bi-check-circle" style={{ fontSize: "3rem", color: "#28a745" }}></i>
            </div>
            <h3 className="mb-3">{t("passwordReset.successTitle")}</h3>
            <p className="text-muted mb-4">
              {t("passwordReset.successMessage")}
            </p>
            <Button variant="primary" onClick={() => navigate("/login")}>
              {t("passwordReset.goToLogin")}
            </Button>
          </Card.Body>
        </Card>
      </Container>
    );
  }

  return (
    <Container className="mt-5">
      <Card style={{ maxWidth: "500px", margin: "0 auto" }}>
        <Card.Body className="p-4">
          <div className="text-center mb-4">
            <i className="bi bi-shield-lock" style={{ fontSize: "2.5rem", color: "#0066cc" }}></i>
            <h3 className="mt-3">{t("passwordReset.resetTitle")}</h3>
            <p className="text-muted">{t("passwordReset.resetSubtitle")}</p>
          </div>

          {error && <Alert variant="danger">{error}</Alert>}

          <Form onSubmit={handleSubmit}>
            <Form.Group className="mb-3">
              <Form.Label>{t("passwordReset.newPasswordLabel")}</Form.Label>
              <InputGroup>
                <Form.Control
                  type={showPassword ? "text" : "password"}
                  placeholder={t("passwordReset.newPasswordPlaceholder")}
                  value={newPassword}
                  onChange={(e) => handlePasswordChange(e.target.value)}
                  required
                  disabled={loading}
                  isInvalid={validationErrors.length > 0}
                  autoComplete="new-password"
                />
                <Button
                  variant="outline-secondary"
                  onClick={() => setShowPassword(!showPassword)}
                  disabled={loading}
                >
                  <i className={`bi bi-eye${showPassword ? "-slash" : ""}`}></i>
                </Button>
              </InputGroup>
              {validationErrors.length > 0 && (
                <Form.Text className="text-danger">
                  <ul className="mb-0 mt-1 ps-3">
                    {validationErrors.map((err, idx) => (
                      <li key={idx}>{err}</li>
                    ))}
                  </ul>
                </Form.Text>
              )}
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>{t("passwordReset.confirmPasswordLabel")}</Form.Label>
              <InputGroup>
                <Form.Control
                  type={showConfirmPassword ? "text" : "password"}
                  placeholder={t("passwordReset.confirmPasswordPlaceholder")}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  disabled={loading}
                  isInvalid={confirmPassword !== "" && newPassword !== confirmPassword}
                  autoComplete="new-password"
                />
                <Button
                  variant="outline-secondary"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  disabled={loading}
                >
                  <i className={`bi bi-eye${showConfirmPassword ? "-slash" : ""}`}></i>
                </Button>
              </InputGroup>
              {confirmPassword !== "" && newPassword !== confirmPassword && (
                <Form.Text className="text-danger">
                  {t("passwordReset.passwordMismatch")}
                </Form.Text>
              )}
            </Form.Group>

            <Button
              variant="primary"
              type="submit"
              className="w-100 mb-3"
              disabled={loading || validationErrors.length > 0 || newPassword !== confirmPassword}
            >
              {loading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2"></span>
                  {t("passwordReset.resetting")}
                </>
              ) : (
                t("passwordReset.resetButton")
              )}
            </Button>
          </Form>
        </Card.Body>
      </Card>
    </Container>
  );
};

export default PasswordResetConfirm;
